import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MegaphoneIcon, ChatBubbleLeftIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { boardAPI } from '../../services/api';
import { BoardPost, boardPostAudienceLabels } from '../../types/board';

/**
 * A főoldali üzenőfal-kivonat: a legutóbbi néhány bejegyzés. Ide érkezik
 * mindenki, így a fal nem azon múlik, eszébe jut-e valakinek megnyitni.
 * Olvasottnak NEM jelöl semmit - az a falon történik.
 */
const HIGHLIGHT_COUNT = 3;

const BoardHighlights: React.FC = () => {
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    boardAPI
      .getPosts({ limit: HIGHLIGHT_COUNT })
      .then((data) => {
        if (!cancelled) setPosts(data);
      })
      .catch((error) => console.error('Az üzenőfal kivonat betöltése nem sikerült:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Üres falnál nem foglalunk helyet a főoldalon
  if (loading || posts.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <MegaphoneIcon className="h-5 w-5 text-primary-600" />
          Üzenőfal
        </h2>
        <Link to="/board" className="text-sm font-medium text-primary-600 hover:underline">
          Összes
        </Link>
      </div>

      <div className="space-y-2">
        {posts.map((post) => (
          <Link
            key={post.id}
            to={`/board/${post.id}`}
            className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-colors hover:bg-gray-50"
          >
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
              {post.isPinned && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-2 py-0.5 text-xs font-medium text-white">
                  <MapPinIcon className="h-3.5 w-3.5" />
                  Kitűzve
                </span>
              )}
              {boardPostAudienceLabels(post).map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800"
                >
                  {label}
                </span>
              ))}
              {!post.isReadByMe && (
                <span className="inline-flex items-center whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  Új
                </span>
              )}
            </div>

            <p className="line-clamp-3 whitespace-pre-wrap break-words hyphens-auto text-sm text-gray-800">
              {post.content}
            </p>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
              <span>{post.authorName}</span>
              <span>{new Date(post.createdAt).toLocaleDateString('hu-HU')}</span>
              {post.commentCount > 0 && (
                <span className="inline-flex items-center gap-1">
                  <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
                  {post.commentCount}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default BoardHighlights;
